import React, { useState } from 'react';
import {
  Search,
  Filter,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit2,
  Trash2,
  X,
  Plus,
  Image,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { ConfirmModal } from '@/components/ui/Modal';
import { ExportModal } from '@/components/export/ExportModal';
import { CategoryForm } from '@/components/categories/CategoryForm';
import { ReceiptDetailModal } from '@/components/receipts/ReceiptDetailModal';
import {
  useTransactions,
  useDeleteTransaction,
  useUpdateTransaction,
} from '@/hooks/useDashboard';
import { useCategories, useCreateCategory } from '@/hooks/useCategories';
import {
  CATEGORY_COLORS,
  type TransactionFilters,
  type TransactionCategory,
  type TransactionType,
  type Transaction,
} from '@/types/transaction';
import type { CreateCategoryInput } from '@/types/category';
import { useTranslation } from '@/i18n';
import type { Locale } from '@/i18n';

const formatCurrency = (value: number, locale: Locale) => {
  const currencyMap: Record<Locale, string> = {
    'pt-BR': 'BRL',
    'en-GB': 'GBP',
    'es-ES': 'EUR',
    'fr-FR': 'EUR',
    'it-IT': 'EUR',
    'ar': 'SAR',
    'zh-CN': 'CNY',
  };
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyMap[locale],
  }).format(value);
};

const formatDate = (dateString: string | undefined | null, locale: Locale) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatFullDate = (dateString: string | undefined | null, locale: Locale) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

// Helper to safely get category key from string or object
const getCategoryKey = (category: unknown): string => {
  if (typeof category === 'string') return category;
  if (category && typeof category === 'object') {
    const cat = category as { name?: string; nameEn?: string; id?: string };
    return cat.nameEn || cat.name || cat.id || 'other';
  }
  return 'other';
};

export const TransactionsPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    limit: 20,
    sortBy: 'date',
    sortOrder: 'desc',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(
    null
  );
  const [editMode, setEditMode] = useState(false);
  const [editedCategory, setEditedCategory] = useState<TransactionCategory | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedAmount, setEditedAmount] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data, isLoading } = useTransactions(filters);
  const deleteTransactionMutation = useDeleteTransaction();
  const updateTransactionMutation = useUpdateTransaction();
  const { data: categoriesData } = useCategories();
  const createCategoryMutation = useCreateCategory();

  // Get custom categories
  const allCategories = categoriesData?.categories || [];

  // Mock data for development
  const mockTransactions: Transaction[] = [
    {
      id: '1',
      userId: 'user123',
      type: 'expense',
      category: 'food',
      amount: 45.9,
      currency: 'BRL',
      description: 'Almoco - Restaurante',
      merchant: 'Restaurante Bom Sabor',
      date: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: ['almoco', 'trabalho'],
    },
    {
      id: '2',
      userId: 'user123',
      type: 'expense',
      category: 'transport',
      amount: 25.0,
      currency: 'BRL',
      description: 'Uber - Casa ao trabalho',
      merchant: 'Uber',
      date: new Date(Date.now() - 86400000).toISOString(),
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: '3',
      userId: 'user123',
      type: 'income',
      category: 'salary',
      amount: 5000.0,
      currency: 'BRL',
      description: 'Salario mensal',
      date: new Date(Date.now() - 172800000).toISOString(),
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 172800000).toISOString(),
    },
    {
      id: '4',
      userId: 'user123',
      type: 'expense',
      category: 'shopping',
      amount: 189.9,
      currency: 'BRL',
      description: 'Compras - Magazine',
      merchant: 'Magazine Luiza',
      date: new Date(Date.now() - 259200000).toISOString(),
      createdAt: new Date(Date.now() - 259200000).toISOString(),
      updatedAt: new Date(Date.now() - 259200000).toISOString(),
      receiptId: 'receipt123',
    },
    {
      id: '5',
      userId: 'user123',
      type: 'expense',
      category: 'entertainment',
      amount: 39.9,
      currency: 'BRL',
      description: 'Netflix',
      merchant: 'Netflix',
      date: new Date(Date.now() - 345600000).toISOString(),
      createdAt: new Date(Date.now() - 345600000).toISOString(),
      updatedAt: new Date(Date.now() - 345600000).toISOString(),
    },
    {
      id: '6',
      userId: 'user123',
      type: 'expense',
      category: 'health',
      amount: 150.0,
      currency: 'BRL',
      description: 'Farmacia',
      merchant: 'Drogasil',
      date: new Date(Date.now() - 432000000).toISOString(),
      createdAt: new Date(Date.now() - 432000000).toISOString(),
      updatedAt: new Date(Date.now() - 432000000).toISOString(),
    },
  ];

  const transactions = data?.transactions || mockTransactions;
  const total = data?.total || mockTransactions.length;
  const totalPages = Math.ceil(total / (filters.limit || 20));

  const handleFilterChange = (key: keyof TransactionFilters, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (value as number),
    }));
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      await deleteTransactionMutation.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
      setSelectedTransaction(null);
    }
  };

  const enterEditMode = (transaction: Transaction) => {
    setEditedCategory(transaction.category);
    setEditedDescription(transaction.description);
    setEditedAmount(transaction.amount.toString());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dateValue = transaction.date || (transaction as any).transactionDate || '';
    setEditedDate(dateValue ? dateValue.split('T')[0] : ''); // Format for date input
    setEditMode(true);
  };

  const exitEditMode = () => {
    setEditMode(false);
    setEditedCategory(null);
    setEditedDescription('');
    setEditedAmount('');
    setEditedDate('');
  };

  const handleUpdate = async (id: string) => {
    const updates: Partial<Transaction> = {};

    if (editedCategory && selectedTransaction && editedCategory !== selectedTransaction.category) {
      updates.category = editedCategory;
    }
    if (editedDescription && selectedTransaction && editedDescription !== selectedTransaction.description) {
      updates.description = editedDescription;
    }
    if (editedAmount && selectedTransaction && parseFloat(editedAmount) !== selectedTransaction.amount) {
      updates.amount = parseFloat(editedAmount);
    }
    if (editedDate && selectedTransaction) {
      const originalDate = selectedTransaction.date.split('T')[0];
      if (editedDate !== originalDate) {
        updates.date = new Date(editedDate).toISOString();
      }
    }

    if (Object.keys(updates).length > 0) {
      await updateTransactionMutation.mutateAsync({ id, data: updates });
    }
    exitEditMode();
  };

  const handleCreateCategory = async (data: CreateCategoryInput) => {
    await createCategoryMutation.mutateAsync(data);
    setShowCategoryForm(false);
  };

  if (isLoading) {
    return <PageLoader message={t.app.transactions.loading} />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t.app.transactions.title}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.transactions.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="w-4 h-4" />}
            onClick={() => setShowExportModal(true)}
          >
            {t.app.common.export}
          </Button>
        </div>
      </div>

      {/* Search and filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={t.app.transactions.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Type filter */}
            <select
              value={filters.type || ''}
              onChange={(e) =>
                handleFilterChange(
                  'type',
                  e.target.value ? (e.target.value as TransactionType) : undefined
                )
              }
              className="input w-full md:w-36"
            >
              <option value="">{t.app.common.type}</option>
              <option value="expense">{t.app.common.expense}</option>
              <option value="income">{t.app.common.income}</option>
            </select>

            {/* Category filter */}
            <select
              value={filters.category || ''}
              onChange={(e) =>
                handleFilterChange(
                  'category',
                  e.target.value ? (e.target.value as TransactionCategory) : undefined
                )
              }
              className="input w-full md:w-44"
            >
              <option value="">{t.app.common.category}</option>
              {Object.keys(t.app.categoryLabels).map((key) => (
                <option key={key} value={key}>
                  {t.app.categoryLabels[key as keyof typeof t.app.categoryLabels]}
                </option>
              ))}
            </select>

            {/* Toggle more filters */}
            <Button
              variant="ghost"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="w-4 h-4" />}
            >
              {t.app.transactions.filters.filters}
            </Button>
          </div>

          {/* Extended filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="label">{t.app.transactions.filters.startDate}</label>
                  <input
                    type="date"
                    value={filters.startDate || ''}
                    onChange={(e) =>
                      handleFilterChange('startDate', e.target.value || undefined)
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">{t.app.transactions.filters.endDate}</label>
                  <input
                    type="date"
                    value={filters.endDate || ''}
                    onChange={(e) =>
                      handleFilterChange('endDate', e.target.value || undefined)
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">{t.app.transactions.filters.minAmount}</label>
                  <input
                    type="number"
                    value={filters.minAmount || ''}
                    onChange={(e) =>
                      handleFilterChange(
                        'minAmount',
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    placeholder={formatCurrency(0, locale)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">{t.app.transactions.filters.maxAmount}</label>
                  <input
                    type="number"
                    value={filters.maxAmount || ''}
                    onChange={(e) =>
                      handleFilterChange(
                        'maxAmount',
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                    placeholder={formatCurrency(0, locale)}
                    className="input"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions list */}
      <Card padding="none">
        {/* Mobile list view */}
        <div className="md:hidden divide-y divide-gray-200 dark:divide-secondary-700">
          {transactions.map((transaction) => (
            <div
              key={transaction.id}
              className="p-4 hover:bg-gray-50 dark:hover:bg-secondary-700/50"
              onClick={() => setSelectedTransaction(transaction)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: `${CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS]}20`,
                    }}
                  >
                    {transaction.type === 'income' ? (
                      <ArrowUpRight
                        className="w-5 h-5"
                        style={{ color: CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS] }}
                      />
                    ) : (
                      <ArrowDownRight
                        className="w-5 h-5"
                        style={{ color: CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS] }}
                      />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {transaction.description}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.date, locale)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {transaction.receiptId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedReceiptId(transaction.receiptId!);
                      }}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                      title={t.app.transactions.viewReceipt || 'View Receipt'}
                    >
                      <Image className="w-4 h-4 text-primary-500" />
                    </button>
                  )}
                  <p
                    className={`font-semibold ${
                      transaction.type === 'income'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {transaction.type === 'income' ? '+' : '-'}
                    {formatCurrency(transaction.amount, locale)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table view */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-secondary-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.description}
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.category}
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.date}
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.amount}
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.receipt || 'Receipt'}
                </th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t.app.transactions.table.actions}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-secondary-700">
              {transactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="hover:bg-gray-50 dark:hover:bg-secondary-700/50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: `${CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS]}20`,
                        }}
                      >
                        {transaction.type === 'income' ? (
                          <ArrowUpRight
                            className="w-4 h-4"
                            style={{ color: CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS] }}
                          />
                        ) : (
                          <ArrowDownRight
                            className="w-4 h-4"
                            style={{ color: CATEGORY_COLORS[getCategoryKey(transaction.category) as keyof typeof CATEGORY_COLORS] }}
                          />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </p>
                        {transaction.merchant && (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {transaction.merchant}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={transaction.type === 'income' ? 'success' : 'default'}
                    >
                      {t.app.categoryLabels[getCategoryKey(transaction.category) as keyof typeof t.app.categoryLabels] || getCategoryKey(transaction.category)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(transaction.date, locale)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`font-semibold ${
                        transaction.type === 'income'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {transaction.type === 'income' ? '+' : '-'}
                      {formatCurrency(transaction.amount, locale)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {transaction.receiptId ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReceiptId(transaction.receiptId!);
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700 inline-flex items-center justify-center"
                        title={t.app.transactions.viewReceipt || 'View Receipt'}
                      >
                        <Image className="w-4 h-4 text-primary-500" />
                      </button>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedTransaction(transaction)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title={t.app.common.viewDetails}
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          enterEditMode(transaction);
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title={t.app.common.edit}
                      >
                        <Edit2 className="w-4 h-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(transaction.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700"
                        title={t.app.common.delete}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-secondary-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t.app.transactions.showing
              .replace('{count}', transactions.length.toString())
              .replace('{total}', total.toString())}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={(filters.page || 1) <= 1}
              onClick={() => handleFilterChange('page', (filters.page || 1) - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t.app.common.page} {filters.page || 1} {t.app.common.of} {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={(filters.page || 1) >= totalPages}
              onClick={() => handleFilterChange('page', (filters.page || 1) + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Transaction detail modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <CardHeader
              action={
                <button
                  onClick={() => {
                    setSelectedTransaction(null);
                    exitEditMode();
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-secondary-700 rounded"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              }
            >
              <CardTitle>
                {editMode ? t.app.transactions.modal.editTitle : t.app.transactions.modal.detailsTitle}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Amount */}
                <div className="text-center py-4">
                  <p
                    className={`text-3xl font-bold ${
                      selectedTransaction.type === 'income'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {selectedTransaction.type === 'income' ? '+' : '-'}
                    {formatCurrency(selectedTransaction.amount, locale)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {formatFullDate(selectedTransaction.date, locale)}
                  </p>
                </div>

                {/* Details grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.common.type}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {selectedTransaction.type === 'income' ? t.app.common.income : t.app.common.expense}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t.app.common.category}
                    </p>
                    {editMode ? (
                      <div className="flex gap-2 mt-1">
                        <select
                          value={editedCategory || getCategoryKey(selectedTransaction.category)}
                          onChange={(e) => setEditedCategory(e.target.value as TransactionCategory)}
                          className="input flex-1"
                        >
                          {Object.keys(t.app.categoryLabels).map((key) => (
                            <option key={key} value={key}>
                              {t.app.categoryLabels[key as keyof typeof t.app.categoryLabels]}
                            </option>
                          ))}
                          {allCategories.filter(c => !c.isSystem).map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowCategoryForm(true)}
                          className="p-2 rounded-lg border border-gray-300 dark:border-secondary-600 hover:bg-gray-100 dark:hover:bg-secondary-700"
                          title={t.app.transactions.modal.createCategory}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <Badge
                        variant={
                          selectedTransaction.type === 'income' ? 'success' : 'default'
                        }
                      >
                        {t.app.categoryLabels[getCategoryKey(selectedTransaction.category) as keyof typeof t.app.categoryLabels] || getCategoryKey(selectedTransaction.category)}
                      </Badge>
                    )}
                  </div>
                  {editMode && (
                    <>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.common.amount}</p>
                        <input
                          type="number"
                          step="0.01"
                          value={editedAmount}
                          onChange={(e) => setEditedAmount(e.target.value)}
                          className="input mt-1"
                        />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t.app.common.date}</p>
                        <input
                          type="date"
                          value={editedDate}
                          onChange={(e) => setEditedDate(e.target.value)}
                          className="input mt-1"
                        />
                      </div>
                    </>
                  )}
                  {selectedTransaction.merchant && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t.app.transactions.modal.merchant}
                      </p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedTransaction.merchant}
                      </p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t.app.transactions.modal.description}
                    </p>
                    {editMode ? (
                      <input
                        type="text"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        className="input mt-1"
                      />
                    ) : (
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedTransaction.description}
                      </p>
                    )}
                  </div>
                  {selectedTransaction.tags && selectedTransaction.tags.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        {t.app.common.tags}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTransaction.tags.map((tag) => (
                          <Badge key={tag} variant="secondary">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedTransaction.receiptId && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t.app.common.receipt}
                      </p>
                      <Badge variant="info" dot>
                        {t.app.common.receiptAttached}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-secondary-700">
                  {editMode ? (
                    <>
                      <Button
                        variant="primary"
                        onClick={() => handleUpdate(selectedTransaction.id)}
                        isLoading={updateTransactionMutation.isPending}
                      >
                        {t.app.common.save}
                      </Button>
                      <Button variant="ghost" onClick={exitEditMode}>
                        {t.app.common.cancel}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="secondary"
                        onClick={() => enterEditMode(selectedTransaction)}
                        leftIcon={<Edit2 className="w-4 h-4" />}
                      >
                        {t.app.common.edit}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeleteClick(selectedTransaction.id)}
                        isLoading={deleteTransactionMutation.isPending}
                        leftIcon={<Trash2 className="w-4 h-4" />}
                      >
                        {t.app.common.delete}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        type="transactions"
      />

      {/* Create Category Modal */}
      <CategoryForm
        isOpen={showCategoryForm}
        onClose={() => setShowCategoryForm(false)}
        onSubmit={handleCreateCategory}
        isLoading={createCategoryMutation.isPending}
      />

      {/* Receipt Detail Modal */}
      <ReceiptDetailModal
        receiptId={selectedReceiptId}
        onClose={() => setSelectedReceiptId(null)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title={t.app.common.confirmTitle || 'Confirmar'}
        message={t.app.transactions.confirmDelete}
        confirmText={t.app.common.delete}
        cancelText={t.app.common.cancel}
        variant="danger"
        isLoading={deleteTransactionMutation.isPending}
      />
    </div>
  );
};

export default TransactionsPage;
